import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Callout, FormSection, Icon, Paragraph, toastError, toastSuccess } from 'admination-react'
import { AskModal } from '@/core/components/ui/ask-modal'
import { formatDatum } from '@/core/lib/dates'
import { friendlyErrorMessage } from '@/core/lib/dbError'
import { useApp } from '@/core/state/appStore'
import { ausAktivierung, getAktivierungStand, inAktivierung, type AktivierungStand } from '../../api/aktivierung'

type Laden = { kind: 'loading' } | { kind: 'error' } | { kind: 'ready'; stand: AktivierungStand }

export function AktivierungSection({ coachingId }: { coachingId: string }) {
  const { t } = useTranslation('vergabeStaff')
  const [laden, setLaden] = useState<Laden>({ kind: 'loading' })
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    let cancelled = false
    setLaden({ kind: 'loading' })
    getAktivierungStand(coachingId)
      .then((stand) => {
        if (!cancelled) setLaden({ kind: 'ready', stand })
      })
      .catch((error: unknown) => {
        if (cancelled) return
        console.error("Aktivierung laden fehlgeschlagen:", error)
        setLaden({ kind: 'error' })
      })
    return () => {
      cancelled = true
    }
  }, [coachingId, attempt])

  if (laden.kind === 'ready' && !laden.stand.drin && !laden.stand.akte_offen) return null

  return (
    <FormSection label={t('profil.aktivierung.section')}>
      <Paragraph variant="muted">{t('profil.aktivierung.hint')}</Paragraph>
      {laden.kind === 'loading' ? (
        <Paragraph variant="muted" aria-busy="true">
          {t('profil.loading')}
        </Paragraph>
      ) : laden.kind === 'error' ? (
        <>
          <Callout tone="error" role="alert">
            {t('profil.aktivierung.ladeFehler')}
          </Callout>
          <div>
            <Button onClick={() => setAttempt((n) => n + 1)}>{t('profil.retry')}</Button>
          </div>
        </>
      ) : laden.stand.drin || laden.stand.versaeumt_vorher > 0 ? (
        <Hebel coachingId={coachingId} stand={laden.stand} />
      ) : (
        <Paragraph data-testid="aktivierung-stand">{t('profil.aktivierung.nichtsVersaeumt')}</Paragraph>
      )}
    </FormSection>
  )
}

function Hebel({ coachingId, stand }: { coachingId: string; stand: AktivierungStand }) {
  const { t } = useTranslation('vergabeStaff')
  const { reloadDashboard } = useApp()
  const [fragen, setFragen] = useState(false)
  const [busy, setBusy] = useState(false)
  const knopf = t(stand.drin ? 'profil.aktivierung.zurueckholen' : 'profil.aktivierung.verschieben')

  async function bewegen() {
    setBusy(true)
    try {
      if (stand.drin) await ausAktivierung(coachingId)
      else await inAktivierung(coachingId)
      toastSuccess(t(stand.drin ? 'profil.aktivierung.erfolgZurueck' : 'profil.aktivierung.erfolgRein'))
    } catch (error) {
      console.error("Aktivierung ändern fehlgeschlagen:", error)
      toastError(friendlyErrorMessage(error, t('profil.aktivierung.fehler')))
    } finally {
      setBusy(false)
      setFragen(false)
      reloadDashboard()
    }
  }

  return (
    <>
      <Paragraph data-testid="aktivierung-stand">
        {!stand.drin
          ? t('profil.aktivierung.versaeumt', { count: stand.versaeumt, seit: formatDatum(stand.erster_versaeumter_tag) })
          : stand.verschoben_von
            ? t('profil.aktivierung.drinVonHand', { seit: formatDatum(stand.eintritt_am), tag: stand.tag, von: stand.verschoben_von, am: formatDatum(stand.verschoben_am) })
            : t('profil.aktivierung.drinAutomatisch', { seit: formatDatum(stand.eintritt_am), tag: stand.tag })}
      </Paragraph>
      {stand.unterschrieben && stand.aufgaben_offen > 0 && (
        <Paragraph data-testid="aktivierung-unterschrieben">{t('profil.aktivierung.unterschriebenOffen', { count: stand.aufgaben_offen })}</Paragraph>
      )}
      <div>
        <Button
          data-testid={stand.drin ? 'aktivierung-zurueckholen' : 'aktivierung-verschieben'}
          disabled={busy}
          onClick={() => setFragen(true)}
        >
          <Icon name={stand.drin ? 'i-arrow-left' : 'i-arrow-right'} />
          {knopf}
        </Button>
      </div>
      <AskModal
        open={fragen}
        icon="i-person"
        iconStroke
        variant="is-warning"
        question={`${knopf}?`}
        description={stand.drin ? t('profil.aktivierung.frageZurueck') : t('profil.aktivierung.frageRein', { count: stand.versaeumt })}
        confirmLabel={knopf}
        cancelLabel={t('profil.rueckzug.cancel')}
        busy={busy}
        onConfirm={() => void bewegen()}
        onCancel={() => setFragen(false)}
      />
    </>
  )
}
